import { useEffect, useRef, useState } from 'react'
import { sendEmail, TEMPLATES } from '../lib/emailjs'

function useFadeIn() {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible') },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return ref
}

const INITIAL = {
  name: '', country: '', email: '',
  model: '', year: '', mileage: '', condition: '',
  budgetMin: '', budgetMax: '', qty: '', notes: '',
}

export default function InquirySection({ t, onInquirySubmit, onInquiryError }) {
  const leftRef = useFadeIn()
  const formRef = useFadeIn()
  const [form, setForm] = useState(INITIAL)
  const [sending, setSending] = useState(false)

  function set(key) {
    return (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email) {
      alert(t.modal_email_required)
      return
    }

    setSending(true)
    try {
      const title = `[A2G Inquiry] ${form.model || 'Vehicle Request'} — ${form.name || 'Dealer'} (${form.country || ''})`

      const message = [
        '─────────────────────────────',
        '  CONTACT INFO',
        '─────────────────────────────',
        `Name     : ${form.name    || '-'}`,
        `Country  : ${form.country || '-'}`,
        `Email    : ${form.email   || '-'}`,
        '',
        '─────────────────────────────',
        '  VEHICLE REQUEST',
        '─────────────────────────────',
        `Model      : ${form.model     || '-'}`,
        `Year       : ${form.year      || '-'}`,
        `Mileage    : ${form.mileage   || '-'}`,
        `Condition  : ${form.condition || '-'}`,
        `Budget Min : ${form.budgetMin || '-'}`,
        `Budget Max : ${form.budgetMax || '-'}`,
        `Quantity   : ${form.qty       || '-'}`,
        '',
        '─────────────────────────────',
        '  ADDITIONAL NOTES',
        '─────────────────────────────',
        form.notes || '(none)',
        '',
        '─────────────────────────────',
      ].join('\n')

      await sendEmail(TEMPLATES.inquiry, {
        email: 'key.eu@datium.xyz',
        title,
        name: form.name,
        message,
      })

      setForm(INITIAL)
      onInquirySubmit()
    } catch (err) {
      console.error('EmailJS error:', err)
      onInquiryError?.()
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="inquiry-section" id="inquiry">
      <div className="section-inner">
        <div className="inquiry-layout">
          <div ref={leftRef} className="inquiry-left fade-in">
            <div className="section-tag">{t.inq_tag}</div>
            <h2>{t.inq_h2_1}<br /><em>{t.inq_h2_em}</em></h2>
            <p>{t.inq_desc}</p>
            <ul className="why-list">
              <li>
                <div className="why-icon blue">🎯</div>
                <div className="why-text">
                  <h4>{t.why1_title}</h4>
                  <p>{t.why1_desc}</p>
                </div>
              </li>
              <li>
                <div className="why-icon gold">🤝</div>
                <div className="why-text">
                  <h4>{t.why2_title}</h4>
                  <p>{t.why2_desc}</p>
                </div>
              </li>
              <li>
                <div className="why-icon green">✅</div>
                <div className="why-text">
                  <h4>{t.why3_title}</h4>
                  <p>{t.why3_desc}</p>
                </div>
              </li>
            </ul>
          </div>

          <div ref={formRef} className="inquiry-form fade-in">
            <div className="form-title">{t.form_title}</div>
            <div className="form-sub">{t.form_sub}</div>

            <form onSubmit={handleSubmit}>
              <div className="form-section-label">{t.form_sec1}</div>

              <div className="form-row">
                <div className="form-grid-2">
                  <div>
                    <label className="form-label">{t.f_name} <span className="req">*</span></label>
                    <input className="form-input" type="text" placeholder={t.f_name_ph} value={form.name} onChange={set('name')} />
                  </div>
                  <div>
                    <label className="form-label">{t.f_country} <span className="req">*</span></label>
                    <select className="form-input" value={form.country} onChange={set('country')}>
                      <option value="">{t.f_country_ph}</option>
                      <option>🇦🇪 UAE</option>
                      <option>🇸🇦 Saudi Arabia</option>
                      <option>🇷🇺 Russia</option>
                      <option>🇰🇿 Kazakhstan</option>
                      <option>🇲🇳 Mongolia</option>
                      <option>🇦🇺 Australia</option>
                      <option>🇳🇿 New Zealand</option>
                      <option>🇳🇬 Nigeria</option>
                      <option>🇬🇭 Ghana</option>
                      <option>{t.f_country_other}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">{t.f_email} <span className="req">*</span></label>
                <input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} required />
              </div>

              <div className="form-divider" />
              <div className="form-section-label">{t.form_sec2}</div>

              <div className="form-row">
                <div className="form-grid-2">
                  <div>
                    <label className="form-label">{t.f_model} <span className="req">*</span></label>
                    <input className="form-input" type="text" placeholder={t.f_model_ph} value={form.model} onChange={set('model')} />
                  </div>
                  <div>
                    <label className="form-label">{t.f_year} <span className="req">*</span></label>
                    <select className="form-input" value={form.year} onChange={set('year')}>
                      <option value="">{t.f_select}</option>
                      <option>{t.yr1}</option><option>{t.yr2}</option>
                      <option>{t.yr3}</option><option>{t.yr4}</option>
                      <option>{t.yr5}</option><option>{t.yr6}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-grid-2">
                  <div>
                    <label className="form-label">{t.f_mileage}</label>
                    <select className="form-input" value={form.mileage} onChange={set('mileage')}>
                      <option value="">{t.f_select}</option>
                      <option>{t.ml1}</option><option>{t.ml2}</option>
                      <option>{t.ml3}</option><option>{t.ml4}</option><option>{t.ml5}</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">{t.f_condition}</label>
                    <select className="form-input" value={form.condition} onChange={set('condition')}>
                      <option value="">{t.f_select}</option>
                      <option>{t.cd1}</option><option>{t.cd2}</option>
                      <option>{t.cd3}</option><option>{t.cd4}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">{t.f_budget} <span className="req">*</span></label>
                <div className="form-grid-2">
                  <input className="form-input" type="text" placeholder={t.f_budget_min} value={form.budgetMin} onChange={set('budgetMin')} />
                  <input className="form-input" type="text" placeholder={t.f_budget_max} value={form.budgetMax} onChange={set('budgetMax')} />
                </div>
              </div>

              <div className="form-row">
                <label className="form-label">{t.f_qty}</label>
                <select className="form-input" value={form.qty} onChange={set('qty')}>
                  <option value="">{t.f_select}</option>
                  <option>{t.qt1}</option><option>{t.qt2}</option>
                  <option>{t.qt3}</option><option>{t.qt4}</option>
                </select>
              </div>

              <div className="form-row">
                <label className="form-label">{t.f_notes}</label>
                <textarea className="form-input" placeholder={t.f_notes_ph} value={form.notes} onChange={set('notes')} />
              </div>

              <button type="submit" className="form-submit" disabled={sending}>
                {sending ? '⏳ Sending...' : t.form_submit}
              </button>
              <div className="form-note">{t.form_note}</div>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}
